import { memo } from "react"
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material"
import { Emoji } from "emoji-picker-react"
import { styles } from "./BadgeHeader.styles"

const splitGraphemeClusters = (str) => {
  if (!str) return []
  if (typeof Intl !== "undefined" && Intl.Segmenter) {
    const segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" })
    return Array.from(segmenter.segment(str), (s) => s.segment)
  }
  const graphemeRegex =
    /\p{RI}\p{RI}|\p{Emoji_Modifier_Base}\p{Emoji_Modifier}|\p{Emoji_Presentation}\uFE0F?\u20E3?|[\u{1F3FB}-\u{1F3FF}]|\p{Emoji}\uFE0F|./gsu
  return str.match(graphemeRegex) || []
}

const emojiGraphemeRegex =
  /^(?:\p{RI}\p{RI}|\p{Emoji_Modifier_Base}\p{Emoji_Modifier}|\p{Emoji_Presentation}\uFE0F?\u20E3?|\p{Emoji}\uFE0F)$/u

const emojiToUnified = (emoji) => {
  if (!emoji) return ""
  const codePoints = []
  for (const char of emoji) {
    const code = char.codePointAt(0)
    if (code !== undefined) {
      codePoints.push(code.toString(16).toLowerCase())
    }
  }
  return codePoints.filter((c) => c !== "fe0f").join("-")
}

const renderBadgeContent = (badgeText) => {
  if (!badgeText) return null
  const items = splitGraphemeClusters(String(badgeText))
  const isEmojiOnly =
    items.length > 0 &&
    items.every((cluster) => emojiGraphemeRegex.test(cluster))

  if (isEmojiOnly) {
    const count = items.length
    const size = count === 1 ? 24 : count === 2 ? 18 : 14
    return (
      <Box sx={styles.emojiContainer(count)}>
        {items.map((cluster, index) => {
          const unified = emojiToUnified(cluster)
          return (
            <Box key={index} component="span" sx={styles.emojiItem(count)}>
              <Emoji unified={unified} size={size} />
            </Box>
          )
        })}
      </Box>
    )
  }

  return badgeText
}

const BadgeHeader = memo(
  ({
    badgeText,
    badgeColor,
    title,
    titleColor,
    subtitle,
    endElement,
    showDivider = false,
    dividerColor,
    reserveDivider = false,
    shortTitle,
    sx,
  }) => {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("xl"))
    const showText = !isMobile || showDivider

    return (
      <Box sx={[styles.container, sx]}>
        <Box sx={styles.headerRow}>
          {badgeText && (
            <Box sx={styles.badge(badgeColor, badgeText)}>
              {renderBadgeContent(badgeText)}
            </Box>
          )}
          {showText && (
            <Box sx={styles.textContainer}>
              <Typography variant="body2Bold" sx={styles.title(titleColor)}>
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="body" sx={styles.subtitle}>
                  {subtitle}
                </Typography>
              )}
            </Box>
          )}
          {endElement && <Box>{endElement}</Box>}
        </Box>
        {(showDivider || reserveDivider) && (
          <Box
            sx={styles.divider(
              showDivider ? dividerColor || badgeColor : "transparent"
            )}
          />
        )}
      </Box>
    )
  }
)

export default BadgeHeader
